import React, { useMemo } from "react";

import { ReportAPI } from "@ai360/core";

import { Checkbox, NumericInput, Section, SelectInput, SubSection } from "~/core";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { messages } from "~/reports/i18n-messages";
import { model, selectors } from "~/reports/data";
import { getReportDataByReportType } from "~/reports/data/selectors";
import { useAppSelector } from "~/store/hooks";
import { ReportOptionsComponentProps } from ".";
import { useIntl } from "react-intl";

import "./field-reference-list.css";

function FieldReferenceList({ hideDefaultOptions, onInputChange }: ReportOptionsComponentProps) {
    const { formatMessage } = useIntl();
    const perPageOptions = useMemo(
        () => [
            {
                label: formatMessage(messages.grower),
                value: "Grower",
            },
            {
                label: formatMessage(messages.allGrowers),
                value: "AllGrowers",
            },
        ],
        [formatMessage]
    );
    const reportData = useAppSelector(getReportDataByReportType(FieldReferenceList.reportName));
    const selectedFields = useAppSelector(selectors.getFields) ?? [];
    const hasFields = selectedFields.length > 0;
    const qrDisabled = !hasFields && reportData[model.PROPS_PER_REPORT_OPTION] === "AllGrowers";

    return (
        <Section className="report-options-content">
            {!hideDefaultOptions ? (
                <SubSection className="two-wide force-wrap">
                    <CroppingSeason reportType={FieldReferenceList.reportName} />
                </SubSection>
            ) : null}
            <SubSection className="two-report-options">
                <Checkbox
                    disabled={false}
                    label={formatMessage(messages.farmTotals)}
                    onChange={(e, value) => onInputChange(model.PROPS_SHOW_FARM_TOTALS, value)}
                    value={reportData[model.PROPS_SHOW_FARM_TOTALS]}
                />
                <Checkbox
                    disabled={false}
                    label={formatMessage(messages.growerTotals)}
                    onChange={(e, value) => onInputChange(model.PROPS_SHOW_GROWER_TOTALS, value)}
                    value={reportData[model.PROPS_SHOW_GROWER_TOTALS]}
                />
            </SubSection>
            <SubSection className="two-report-options">
                <Checkbox
                    disabled={qrDisabled}
                    label={formatMessage(messages.showQrCode)}
                    onChange={(e, value) => onInputChange(model.PROPS_SHOW_QR_CODES, value)}
                    value={reportData[model.PROPS_SHOW_QR_CODES]}
                />
                <SelectInput
                    required
                    clearable={false}
                    containerClassNames={["display-options-list"]}
                    placeholderText={formatMessage(messages.perReportOptions)}
                    options={perPageOptions}
                    onChange={(value) => onInputChange(model.PROPS_PER_REPORT_OPTION, value)}
                    value={reportData[model.PROPS_PER_REPORT_OPTION]}
                />
            </SubSection>
            <SubSection className="two-report-options">
                <Checkbox
                    disabled={false}
                    label={formatMessage(messages.onlyDifferentBillableArea)}
                    onChange={(e, value) =>
                        onInputChange(model.PROPS_ONLY_DIFFERENT_BILLABLE_AREA, value)
                    }
                    value={reportData[model.PROPS_ONLY_DIFFERENT_BILLABLE_AREA]}
                />
                <NumericInput
                    scale={3}
                    precision={7}
                    className="field-reference-list-billable-acres-threshold"
                    disabled={!reportData[model.PROPS_ONLY_DIFFERENT_BILLABLE_AREA]}
                    placeholderText={formatMessage(messages.areaDifferenceThreshold)}
                    onChange={(e, value) =>
                        onInputChange(
                            model.PROPS_AREA_DIFFERENCE_THRESHOLD,
                            value === "" ? null : value
                        )
                    }
                    value={reportData[model.PROPS_AREA_DIFFERENCE_THRESHOLD]}
                />
            </SubSection>
        </Section>
    );
}

FieldReferenceList.defaultReportOptions = {
    [model.PROPS_AREA_DIFFERENCE_THRESHOLD]: null,
    [model.PROPS_CROPPING_SEASON_GUID]: null,
    [model.PROPS_ONLY_DIFFERENT_BILLABLE_AREA]: false,
    [model.PROPS_PER_REPORT_OPTION]: "AllGrowers",
    [model.PROPS_SHOW_FARM_TOTALS]: true,
    [model.PROPS_SHOW_GROWER_TOTALS]: true,
    [model.PROPS_SHOW_QR_CODES]: false,
};

FieldReferenceList.reportPreferences = [
    model.PROPS_PER_REPORT_OPTION,
    model.PROPS_SHOW_FARM_TOTALS,
    model.PROPS_SHOW_GROWER_TOTALS,
    model.PROPS_SHOW_QR_CODES,
];

FieldReferenceList.reportName = ReportAPI.ReportNames.AGVANCE_SKY_FIELD_REFERENCE_LIST;

export default FieldReferenceList;
